/**
 * TagEditorPopover — Notion-style tag editor for a single conversation.
 *
 * Props:
 *   - currentTags: string[]      currently applied tags
 *   - allTags: {tag, count}[]    autocomplete suggestions (from /tags endpoint)
 *   - onSave: (newTags) => void  called when user commits a change
 *   - trigger: React node        what to render as the popover trigger
 */

import { useEffect, useState, useMemo } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Input } from './ui/input';
import { Tag as TagIcon, X, Plus, Check } from 'lucide-react';
import { cn } from '../lib/utils';

export function TagEditorPopover({ currentTags = [], allTags = [], onSave, trigger }) {
  const [open, setOpen] = useState(false);
  const [tags, setTags] = useState(currentTags);
  const [query, setQuery] = useState('');

  useEffect(() => { if (open) setTags(currentTags); }, [open, currentTags]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = allTags.filter((t) => !tags.includes(t.tag));
    if (!q) return base.slice(0, 10);
    return base.filter((t) => t.tag.includes(q)).slice(0, 10);
  }, [allTags, tags, query]);

  const canCreate = useMemo(() => {
    const q = query.trim().toLowerCase().slice(0, 32);
    return q.length > 0 && !tags.includes(q) && !allTags.some((t) => t.tag === q);
  }, [query, tags, allTags]);

  const addTag = (raw) => {
    const t = (raw || '').trim().toLowerCase().slice(0, 32);
    if (!t || tags.includes(t)) return;
    setTags([...tags, t]);
    setQuery('');
  };

  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const commit = () => {
    const same = tags.length === currentTags.length && tags.every((t) => currentTags.includes(t));
    if (!same) onSave?.(tags);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { if (!o) commit(); setOpen(o); }}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-3"
        onClick={(e) => e.stopPropagation()}
        data-testid="tag-editor-popover"
      >
        <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-2 flex items-center gap-1.5">
          <TagIcon className="w-3 h-3" /> Edit tags
        </p>

        {/* Current tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2 pb-2 border-b">
            {tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[11px] font-medium"
                data-testid={`tag-current-${t}`}
              >
                {t}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeTag(t); }}
                  className="hover:text-red-500"
                  aria-label={`Remove ${t}`}
                  data-testid={`tag-remove-${t}`}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (canCreate) addTag(query);
              else if (suggestions[0]) addTag(suggestions[0].tag);
            }
          }}
          placeholder="Search or create tag…"
          className="h-8 text-xs"
          data-testid="tag-editor-input"
        />

        {/* Suggestions list */}
        <div className="mt-2 max-h-60 overflow-y-auto space-y-0.5">
          {suggestions.map((s) => (
            <button
              key={s.tag}
              type="button"
              onClick={() => addTag(s.tag)}
              className="w-full text-left flex items-center gap-2 px-2 py-1 rounded hover:bg-muted text-xs"
              data-testid={`tag-suggest-${s.tag}`}
            >
              <TagIcon className="w-3 h-3 text-muted-foreground" />
              <span className="flex-1">{s.tag}</span>
              <span className="text-[10px] text-muted-foreground">{s.count}</span>
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              onClick={() => addTag(query)}
              className={cn(
                'w-full text-left flex items-center gap-2 px-2 py-1 rounded hover:bg-emerald-50 dark:hover:bg-emerald-950/30 text-xs',
                'text-emerald-700 dark:text-emerald-300',
              )}
              data-testid="tag-create-new"
            >
              <Plus className="w-3 h-3" />
              <span className="flex-1">Create "{query.trim().toLowerCase().slice(0, 32)}"</span>
            </button>
          )}
          {!canCreate && suggestions.length === 0 && (
            <p className="text-[11px] text-muted-foreground text-center py-2 italic">No tags yet — type to create one.</p>
          )}
        </div>

        <button
          type="button"
          onClick={commit}
          className="mt-3 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-md bg-foreground text-background text-xs font-medium hover:opacity-90"
          data-testid="tag-editor-done"
        >
          <Check className="w-3 h-3" /> Done
        </button>
      </PopoverContent>
    </Popover>
  );
}
